import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  addProjectMember,
  getProject,
  listMemberCandidates,
  onWrite,
  removeProjectMember,
  updateProject,
  updateProjectMember,
  type MemberCandidate,
  type ProjectCapabilities,
  type ProjectDetail as Detail,
  type ProjectMember,
} from '../api.js';
import {
  PROJECT_STATUS_LABELS,
  ROLES,
  STAGE_LABELS,
  TYPE_LABELS,
  canHoldFinanceView,
  defaultFinanceView,
  formatDate,
  formatMoney,
  roleLabel,
  type Role,
} from '../labels.js';
import { AppLink } from '../link.js';
import { Lifecycle } from './Lifecycle.js';
import { MyWorkOnThisJob } from './MyWorkOnThisJob.js';
import { NeedsYouNow } from './NeedsYouNow.js';
import { ProjectMembersProvider, useProjectMembersState } from './ProjectMembers.js';
import { ProjectStagesProvider } from './ProjectStages.js';
import { Pulse } from './Pulse.js';
import { Locations } from './Locations.js';
import { WorkPackages } from './WorkPackages.js';
import { Budget } from './Budget.js';
import { Documents } from './Documents.js';
import { Drawings } from './Drawings.js';
import { Billing } from './Billing.js';
import { Costs } from './Costs.js';
import { Variations } from './Variations.js';
import { Materials } from './Materials.js';
import { Milestones } from './Milestones.js';
import { Activities } from './Activities.js';
import { Quality } from './Quality.js';
import { Handover } from './Handover.js';
import { Photos } from './Photos.js';
import { Reports } from './Reports.js';
import { Snags } from './Snags.js';
import { Timeline } from './Timeline.js';
import { Submittals } from './Submittals.js';
import { SiteDiary } from './SiteDiary.js';
import { ON_SITE } from './SiteHome.js';
import { Tasks } from './Tasks.js';
import {
  SECTIONS,
  recordRegisterFrom,
  registerAnchor,
  sectionHref,
  type SectionKey,
} from './project-sections.js';

interface Props {
  readonly projectId: string;
  /** Which section of the page — from `/projects/<id>/<section>`. */
  readonly section: SectionKey;
  /**
   * One record, as its own page, from the last two segments of
   * `/projects/<id>/<section>/<register>/<record>`.
   *
   * The register RENDERS the page — see `Materials.tsx`, where the record page
   * is a mode of the register rather than a screen beside it. All this file does
   * is hand the id down and keep everything above it unchanged: the project
   * head, the stage strip and the tab strip stay, because somebody looking at a
   * material has not left the project.
   */
  readonly record?: { readonly register: string; readonly id: string } | null;
  /** Needed for the "Mine" task filter — who "mine" refers to. */
  readonly currentUserId: string;
  /**
   * True during an operator support visit (ADR-041). The money screens say
   * "this visit cannot see money" rather than blaming the reader's access.
   */
  readonly visiting?: boolean;
}

// The team's four states moved to `ProjectMembers.tsx` with the loader that
// produces them (`MembersState`) — the page and the sections read the same one.

const NO_CAPABILITIES: ProjectCapabilities = {
  add_member: false,
  edit_member: false,
  remove_member: false,
  add_location: false,
  edit_location: false,
  remove_location: false,
  write_diary: false,
  edit_diary: false,
  delete_diary: false,
  add_drawing: false,
  edit_drawing: false,
  remove_drawing: false,
  export_drawing: false,
  add_submittal: false,
  edit_submittal: false,
  remove_submittal: false,
  export_submittal: false,
  override_pulse: false,
  edit_budget: false,
  request_inspection: false,
  conduct_inspection: false,
  raise_ncr: false,
  edit_ncr: false,
  raise_snag: false,
  edit_snag: false,
  remove_snag: false,
  export_snag: false,
  add_material: false,
  edit_material: false,
  remove_material: false,
  export_material: false,
  add_milestone: false,
  edit_milestone: false,
  remove_milestone: false,
  add_document: false,
  edit_document: false,
  remove_document: false,
  export_document: false,
  raise_variation: false,
  edit_variation: false,
  export_variation: false,
  record_cost: false,
  remove_cost: false,
  export_cost: false,
  raise_invoice: false,
  edit_invoice: false,
  add_activity: false,
  edit_activity: false,
  remove_activity: false,
  waive_activity: false,
  export_activity: false,
  export_task: false,
  add_task: false,
  view_pulse: false,
  view_stages: false,
  view_milestones: false,
  write_handover: false,
  edit_handover: false,
  remove_handover: false,
  sign_handover: false,
  send_rfq: false,
  record_delivery: false,
  generate_report: false,
  edit_report: false,
  issue_report: false,
};

export function ProjectDetail({
  projectId,
  section,
  record = null,
  currentUserId,
  visiting = false,
}: Props): JSX.Element {
  const [project, setProject] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingDetails, setEditingDetails] = useState(false);

  const load = useCallback(async () => {
    try {
      setProject(await getProject(projectId));
      setError(null);
    } catch (caught) {
      setProject(null);
      setError(
        caught instanceof ApiError && caught.status === 404
          ? 'That project does not exist, or you do not have access to it.'
          : 'Could not load the project.',
      );
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Read it again whenever anything is written to THIS project.
   *
   * The stage strip below is part of the project, not part of the register the
   * person is looking at — and a register write can move a stage without ever
   * touching this component: requesting the first inspection starts QA/QC, the
   * first material starts Procurement (ADR-043). The register refetched itself
   * and looked right while the strip went on naming yesterday's stages.
   *
   * Matched on the project id rather than the exact route, because every
   * project-scoped path carries it and the list of routes that can move a stage
   * is exactly the list somebody will add to without looking here.
   */
  useEffect(
    () => onWrite((path) => { if (path.includes(projectId)) void load(); }),
    [projectId, load],
  );

  // Left-click navigates in place; Ctrl/Cmd-click and middle-click keep the
  // browser's own behaviour — see `AppLink`, which is where those eight lines
  // live now that a dozen cross-links need them too.
  const back = (
    <p className="breadcrumb">
      <AppLink to="/projects">← Projects</AppLink>
    </p>
  );

  if (error) {
    return (
      <section>
        {back}
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      </section>
    );
  }

  if (!project) {
    return (
      <section>
        {back}
        <p aria-live="polite">Loading project…</p>
      </section>
    );
  }

  // `contract_value` is ABSENT, not null, for a caller without finance:view —
  // so the test is `in`, and the whole row disappears rather than showing a
  // blank where money would be (ADR-005).
  const showsMoney = 'contract_value' in project;
  const can = project.can ?? NO_CAPABILITIES;
  /*
   * Somebody the Overview is not about. A subcontractor holds none of the
   * three reads it is made of, and five boxes each saying "your role does not
   * include" read as a broken page (the owner, 2026-09-04). They get the
   * counts of THEIR work instead — `MyWorkOnThisJob`.
   */
  const ownWorkOnly = !can.view_pulse && !can.view_stages && !can.view_milestones;

  const currency = project.currency ?? 'AED';

  /** A register, with the anchor a cross-link from another section aims at. */
  const register = (key: string, body: JSX.Element): JSX.Element => (
    <div className="register" id={registerAnchor(key)}>
      {body}
    </div>
  );

  /*
   * Named once because it is spent twice — on the register and on one material's
   * own page. A second literal here is a second place to forget `export_material`
   * when the matrix changes, and the symptom of that is an export button that is
   * silently absent on one screen and present on the other.
   */
  const materialCan = {
    write: can.add_material,
    edit: can.edit_material,
    remove: can.remove_material,
    exportRows: can.export_material,
    rfq: can.send_rfq,
    deliver: can.record_delivery,
  };

  const drawingCan = {
    write: can.add_drawing,
    edit: can.edit_drawing,
    remove: can.remove_drawing,
    exportRows: can.export_drawing,
  };

  const submittalCan = {
    write: can.add_submittal,
    edit: can.edit_submittal,
    remove: can.remove_submittal,
    exportRows: can.export_submittal,
  };

  const snagCan = {
    write: can.raise_snag,
    edit: can.edit_snag,
    remove: can.remove_snag,
    exportRows: can.export_snag,
  };

  const activityCan = {
    write: can.add_activity,
    edit: can.edit_activity,
    remove: can.remove_activity,
    waive: can.waive_activity,
    exportRows: can.export_activity,
  };

  const variationCan = {
    write: can.raise_variation,
    edit: can.edit_variation,
    exportRows: can.export_variation,
  };

  const qualityCan = {
    request: can.request_inspection,
    conduct: can.conduct_inspection,
    raiseNcr: can.raise_ncr,
    editNcr: can.edit_ncr,
  };

  /**
   * A register rendering ONE of its records as a page.
   *
   * NOT wrapped in `register(...)`: the record page brings its own frame — a
   * head, a two-column body and a link back — and putting that inside the
   * register's bordered card would be a card inside a card, which is the first
   * thing the 2026 pass took out of this product.
   *
   * A register missing from here is one whose records are a drawer on purpose.
   * `RECORD_PAGES` in `project-sections.ts` is the decision and says why.
   */
  const recordPages: Readonly<Record<string, (recordId: string) => JSX.Element>> = {
    materials: (recordId) => (
      <Materials projectId={projectId} currency={currency} recordId={recordId} can={materialCan} />
    ),
    drawings: (recordId) => <Drawings projectId={projectId} recordId={recordId} can={drawingCan} />,
    submittals: (recordId) => (
      <Submittals projectId={projectId} recordId={recordId} can={submittalCan} />
    ),
    snags: (recordId) => <Snags projectId={projectId} recordId={recordId} can={snagCan} />,
    tasks: (recordId) => (
      <Tasks
        projectId={projectId}
        recordId={recordId}
        currentUserId={currentUserId}
        canExport={can.export_task}
        canAdd={can.add_task}
      />
    ),
    activities: (recordId) => (
      <Activities projectId={projectId} recordId={recordId} can={activityCan} />
    ),
    variations: (recordId) => (
      <Variations
        projectId={projectId}
        currency={currency}
        recordId={recordId}
        can={variationCan}
      />
    ),
    /*
     * TWO KEYS, ONE COMPONENT. `Quality.tsx` renders both registers, so it takes
     * the register alongside the id — and the map key is what supplies it, which
     * is why this is the one entry where the register is named twice.
     */
    inspections: (recordId) => (
      <Quality
        projectId={projectId}
        record={{ register: 'inspections', id: recordId }}
        can={qualityCan}
      />
    ),
    ncrs: (recordId) => (
      <Quality projectId={projectId} record={{ register: 'ncrs', id: recordId }} can={qualityCan} />
    ),
  };

  /*
   * The record the address asks for, if it asks for a real one.
   *
   * `recordRegisterFrom` refuses a register that does not belong to the section
   * beside it, so a truncated or hand-typed link falls back to the section — the
   * same graceful answer `sectionFrom` gives a mistyped section, rather than an
   * error page about an address nobody meant to visit.
   */
  const asked = record && recordRegisterFrom(section, record.register) ? record : null;
  const recordPage = asked ? recordPages[asked.register] : undefined;

  const sections: Readonly<Record<SectionKey, () => JSX.Element>> = {
    /*
     * THE OVERVIEW IS TWO COLUMNS ON A WIDE SCREEN, and that is a layout
     * decision with a reason rather than a way to fill space.
     *
     * The left column is the project's STATE — what it wants from you, how
     * healthy it is, where it has got to. The right is its FACTS: the details,
     * who is on it, the dates. State is what you came for and changes daily;
     * facts are reference and change monthly. Stacking them put 4,876 pixels
     * between the first and the last.
     *
     * Below 1100px the rail folds under the main column and the reading order
     * is unchanged, because it is the source order.
     */
    overview: () => (
      <div className="split">
        {/*
         * SPANS BOTH COLUMNS (`.split > .needs` in sections.css), and it was
         * measured into that rather than designed into it.
         *
         * Inside `.split-main` the panel had 588px to work in, so every row's
         * evidence-and-consequence line wrapped to two and the panel came out
         * 1,023px tall — a whole screen, at the top of the page whose original
         * complaint was its height. Given the full width each row is one line
         * plus its action, which is what the panel is for: read in a glance, or
         * it is just another register.
         */}
        {ownWorkOnly ? (
          <div className="split-main">
            <MyWorkOnThisJob projectId={projectId} />
          </div>
        ) : (
          <>
            <NeedsYouNow projectId={projectId} />

            <div className="split-main">
              {register('pulse', <Pulse projectId={projectId} can={{ override: can.override_pulse }} />)}

              {register('lifecycle', <Lifecycle projectId={projectId} onChanged={() => void load()} />)}

              {register(
                'milestones',
                <Milestones
                  projectId={projectId}
                  can={{
                    write: can.add_milestone,
                    edit: can.edit_milestone,
                    remove: can.remove_milestone,
                  }}
                />,
              )}
            </div>
          </>
        )}

        <aside className="split-rail">
          {register(
            'details',
          <>
            <div className="section-head sub-head-row">
              <h3 className="sub-head">Details</h3>
              {can.edit_project && !editingDetails && (
                <button type="button" className="inline" onClick={() => setEditingDetails(true)}>
                  Edit details
                </button>
              )}
            </div>

            {editingDetails ? (
              <DetailsForm
                project={project}
                showsMoney={showsMoney}
                currency={currency}
                onCancel={() => setEditingDetails(false)}
                onSaved={(updated) => {
                  setProject(updated);
                  setEditingDetails(false);
                }}
              />
            ) : (
              <dl className="detail-grid">
                <dt>Client</dt>
                <dd>{project.client_name ?? '—'}</dd>
                <dt>Start date</dt>
                <dd>{formatDate(project.start_date)}</dd>
                <dt>Target completion</dt>
                <dd>{formatDate(project.target_completion_date)}</dd>
                {showsMoney && (
                  <>
                    <dt>Contract value</dt>
                    <dd className="mono">
                      {currency} {formatMoney(project.contract_value)}
                    </dd>
                  </>
                )}
              </dl>
            )}
          </>,
        )}

          {/* The team list is the fourth read a subcontractor does not hold. */}
          {!ownWorkOnly &&
            register(
              'team',
              <Team projectId={projectId} can={can} onNeedsReload={() => void load()} />,
            )}
        </aside>
      </div>
    ),

    design: () => (
      <>
        {register('drawings', <Drawings projectId={projectId} can={drawingCan} />)}

        {register('submittals', <Submittals projectId={projectId} can={submittalCan} />)}
      </>
    ),

    procurement: () => register('materials', <Materials projectId={projectId} currency={currency} can={materialCan} />),

    site: () => (
      <>
        {register(
          'diary',
          <SiteDiary
            projectId={projectId}
            can={{
              write: can.write_diary,
              edit: can.edit_diary,
              remove: can.delete_diary,
            }}
          />,
        )}

        {register('activities', <Activities projectId={projectId} can={activityCan} />)}

        {register(
          'tasks',
          <Tasks
            projectId={projectId}
            currentUserId={currentUserId}
            canExport={can.export_task}
            canAdd={can.add_task}
          />,
        )}

        {register('photos', <Photos projectId={projectId} />)}
      </>
    ),

    quality: () => (
      <>
        {register('quality', <Quality projectId={projectId} can={qualityCan} />)}

        {register('snags', <Snags projectId={projectId} can={snagCan} />)}

        {register(
          'handover',
          <Handover
            projectId={projectId}
            can={{
              write: can.write_handover,
              edit: can.edit_handover,
              remove: can.remove_handover,
              sign: can.sign_handover,
            }}
          />,
        )}
      </>
    ),

    // The project's own currency, not a hardcoded "AED". A company in Riyadh
    // sets SAR and every money field has to say so — a form labelled in the
    // wrong currency is a form somebody types the wrong number into.
    commercial: () => (
      <>
        {register(
          'budget',
          <Budget
            projectId={projectId}
            currency={currency}
            can={{ edit: can.edit_budget }}
            visiting={visiting}
          />,
        )}

        {register(
          'costs',
          <Costs
            projectId={projectId}
            currency={currency}
            visiting={visiting}
            can={{
              write: can.record_cost,
              remove: can.remove_cost,
              exportRows: can.export_cost,
            }}
          />,
        )}

        {register(
          'variations',
          <Variations projectId={projectId} currency={currency} can={variationCan} />,
        )}

        {register(
          'billing',
          <Billing
            projectId={projectId}
            currency={currency}
            visiting={visiting}
            can={{ write: can.raise_invoice, edit: can.edit_invoice }}
          />,
        )}
      </>
    ),

    records: () => (
      <>
        {register(
          'documents',
          <Documents
            projectId={projectId}
            can={{
              write: can.add_document,
              edit: can.edit_document,
              remove: can.remove_document,
              exportRows: can.export_document,
            }}
          />,
        )}

        {register(
          'reports',
          <Reports
            projectId={projectId}
            currency={currency}
            can={{
              generate: can.generate_report,
              edit: can.edit_report,
              issue: can.issue_report,
            }}
          />,
        )}

        {register('timeline', <Timeline projectId={projectId} />)}

        {register('locations', <Locations projectId={projectId} can={can} />)}

        {/*
          Beside Locations, and for the same reason: both are project STRUCTURE
          that the other registers point at — set up once, edited rarely — as
          opposed to the work itself, which fills the other six sections.
        */}
        {register(
          'work-packages',
          // `=== true`, because an absent capability means NO. A missing flag
          // read as truthy-ish would offer a button the server then refuses.
          <WorkPackages projectId={projectId} can={{ edit: can.edit_project === true }} />,
        )}
      </>
    ),
  };

  return (
    // One member fetch and one stages fetch for the whole page. Sections used
    // to ask separately; they still work standalone, which is how every screen
    // test renders them (see ProjectMembers.tsx).
    <ProjectMembersProvider projectId={projectId}>
      {/*
        `active` is what makes the gate re-read itself when the reader comes
        back to the Overview. Everything a gate item counts — a budget line, a
        drawing, a snag — is created on another tab, and this provider outlives
        a tab change. See the note on `active` in `ProjectStages.tsx`.
      */}
      <ProjectStagesProvider projectId={projectId} active={section === 'overview'}>
        <section>
          {back}

          <div className="project-head">
            <div className="section-head">
              <h2>
                <span className="mono">{project.code}</span> · {project.name}
              </h2>
              {/*
                The way to the site home. Offered on every device, not only a
                phone: somebody sends this link TO the person walking the floor,
                and a link that exists only on the recipient's screen cannot be
                sent to them.
              */}
              <AppLink
                className="secondary inline"
                to={`/projects/${encodeURIComponent(projectId)}/${ON_SITE}`}
              >
                On site
              </AppLink>
            </div>

            <p className="signed-in-as">
              {project.client_name ?? 'No client recorded'} ·{' '}
              {TYPE_LABELS[project.project_type] ?? project.project_type} ·{' '}
              {PROJECT_STATUS_LABELS[project.status] ?? project.status}
            </p>

            {/* A role denied the stages is not shown their names in the header either. */}
            {can.view_stages && <StageStrip project={project} />}
          </div>

          <SectionNav projectId={projectId} current={section} />

          {recordPage && asked ? recordPage(asked.id) : sections[section]()}
        </section>
      </ProjectStagesProvider>
    </ProjectMembersProvider>
  );
}

// ---------------------------------------------------------------------------

/**
 * The seven sections, as tabs.
 *
 * Real links, so a section can be opened in a new tab, bookmarked and sent to
 * somebody. `aria-current="page"` rather than a class alone: the highlight is
 * the only thing saying where you are, and a class is invisible to a screen
 * reader.
 */
function SectionNav({
  projectId,
  current,
}: {
  readonly projectId: string;
  readonly current: SectionKey;
}): JSX.Element {
  return (
    <nav className="section-nav" aria-label="Project sections">
      {SECTIONS.map((s) => (
        <AppLink
          key={s.key}
          to={sectionHref(projectId, s.key)}
          className={s.key === current ? 'section-tab current' : 'section-tab'}
          aria-current={s.key === current ? 'page' : undefined}
        >
          {s.label}
          {/*
            What is in here, as hidden text rather than a `title`.
            WCAG 2.2 §2.5.3 (Label in Name) wants the accessible name to
            CONTAIN the visible one, and a `title` of "Drawings and submittals"
            on a tab that reads "Design" replaces it instead of extending it.
            This way the name is "Design — Drawings and submittals", the
            tooltip is gone, and nobody is told a different word from the one
            they can see.
          */}
          <span className="visually-hidden"> — {s.holds}</span>
        </AppLink>
      ))}
    </nav>
  );
}

// ---------------------------------------------------------------------------

/**
 * Where the job has got to, in one line, on every section.
 *
 * The eight stages OVERLAP (ADR-002), so this is not a progress bar and does
 * not claim a percentage: it names the stages that are running and counts the
 * ones that are finished. The full strip, with its gate checks and its moves,
 * is the Overview's own register.
 */
function StageStrip({ project }: { readonly project: Detail }): JSX.Element | null {
  const stages = project.stages ?? [];
  if (stages.length === 0) return null;

  const done = stages.filter((s) => s.status === 'complete').length;
  const running = stages
    .filter((s) => s.status === 'active' || s.status === 'at_risk' || s.status === 'blocked')
    .map((s) => STAGE_LABELS[s.stage_key] ?? s.stage_key);

  return (
    <p className="stage-strip">
      <span className="stage-strip-count">
        {done} of {stages.length} stages complete
      </span>
      {running.length > 0 && (
        <span className="stage-strip-running">
          {' · '}
          <strong>{running.join(', ')}</strong> running
        </span>
      )}
    </p>
  );
}

// ---------------------------------------------------------------------------

/**
 * Correcting the project's own details.
 *
 * This form is why four Award gate items could be satisfied at all. They ask
 * for a client, a contract value and two dates; all four were settable only at
 * creation, so "Client set ✗" was a sentence pointing at nothing. `04` gives
 * `project: E` to Company Admin, Director and PM, and the button follows that.
 *
 * The contract value is typed as text and sent as a decimal STRING — the route
 * refuses a JSON number, because a JSON number is a float (`07` §DI2).
 */
function DetailsForm({
  project,
  showsMoney,
  currency,
  onCancel,
  onSaved,
}: {
  readonly project: Detail;
  readonly showsMoney: boolean;
  readonly currency: string;
  readonly onCancel: () => void;
  readonly onSaved: (project: Detail) => void;
}): JSX.Element {
  const [name, setName] = useState(project.name);
  const [client, setClient] = useState(project.client_name ?? '');
  const [value, setValue] = useState(project.contract_value ?? '');
  const [start, setStart] = useState(project.start_date ?? '');
  const [target, setTarget] = useState(project.target_completion_date ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await updateProject(project.id, {
          name: name.trim(),
          client_name: client.trim() === '' ? null : client.trim(),
          // Omitted entirely without financial access. A caller who cannot READ
          // the figure must not be able to blank it — the same rule the
          // materials register learned (`04` §Financial field gating).
          ...(showsMoney ? { contract_value: value.trim() === '' ? null : value.trim() } : {}),
          start_date: start === '' ? null : start,
          target_completion_date: target === '' ? null : target,
        }),
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={(e) => void submit(e)}>
      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      <div className="field">
        <label htmlFor="project-name">Project name</label>
        <input
          id="project-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          required
          disabled={busy}
        />
      </div>

      <div className="field">
        <label htmlFor="project-client">Client</label>
        <input
          id="project-client"
          value={client}
          onChange={(e) => setClient(e.target.value)}
          maxLength={200}
          disabled={busy}
        />
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="project-start">Start date</label>
          <input
            id="project-start"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label htmlFor="project-target">Target completion</label>
          <input
            id="project-target"
            type="date"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      {showsMoney && (
        <div className="field">
          <label htmlFor="project-value">Contract value ({currency})</label>
          <input
            id="project-value"
            inputMode="decimal"
            pattern="^([0-9]+(\.[0-9]{1,4})?)?$"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={busy}
          />
          <p className="hint">Digits and up to four decimal places. No thousands separators.</p>
        </div>
      )}

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy}>
          {busy ? 'Saving…' : 'Save details'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}


// ---------------------------------------------------------------------------

interface TeamProps {
  readonly projectId: string;
  readonly can: ProjectCapabilities;
  /** Financial access can change what the project page itself may show. */
  readonly onNeedsReload: () => void;
}

function Team({ projectId, can, onNeedsReload }: TeamProps): JSX.Element {
  // The page's one member fetch — and its own, when this renders standalone.
  // A consultant may legitimately see the project and not its staffing list,
  // so this is the one consumer that reads the full state rather than a list.
  const { state, replace } = useProjectMembersState(projectId);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">
        Team{state.status === 'ready' ? ` (${state.members.length})` : ''}
      </h3>
      {can.add_member && state.status === 'ready' && !adding && (
        <button type="button" className="inline" onClick={() => setAdding(true)}>
          Add someone
        </button>
      )}
    </div>
  );

  if (state.status === 'loading') {
    return (
      <>
        {head}
        <p aria-live="polite">Loading the team…</p>
      </>
    );
  }

  if (state.status === 'not_permitted') {
    return (
      <>
        {head}
        <p className="hint">Your role does not include the project team list.</p>
      </>
    );
  }

  if (state.status === 'failed') {
    return (
      <>
        {head}
        <div className="alert" role="alert">
          <span>{state.message}</span>
        </div>
      </>
    );
  }

  /** Run a team write, showing the server's own message when it refuses. */
  async function run(work: () => Promise<readonly ProjectMember[]>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      // The server hands back the new list, so the shared state is replaced
      // rather than re-fetched.
      replace(await work());
      // Granting or revoking financial access changes whether this page may
      // show the contract value — so the project is re-read, not patched.
      onNeedsReload();
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const members = state.members;

  return (
    <>
      {head}

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {adding && (
        <AddMemberForm
          projectId={projectId}
          busy={busy}
          onCancel={() => {
            setAdding(false);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => addProjectMember(projectId, input))) setAdding(false);
          }}
        />
      )}

      {members.length === 0 ? (
        <div className="empty">
          <p>
            <strong>Nobody is on this project yet.</strong>
          </p>
        </div>
      ) : (
        <table className="grid">
          <caption className="visually-hidden">People on this project</caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Role on this project</th>
              <th scope="col">Financial access</th>
              {(can.edit_member || can.remove_member) && (
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {members.map((member) =>
              editing === member.user_id ? (
                <EditMemberRow
                  key={member.user_id}
                  member={member}
                  busy={busy}
                  onCancel={() => setEditing(null)}
                  onSubmit={async (input) => {
                    if (await run(() => updateProjectMember(projectId, member.user_id, input))) {
                      setEditing(null);
                    }
                  }}
                />
              ) : (
                <tr key={member.user_id}>
                  {/*
                    `data-label` is what lets this table become CARDS when its
                    own card is under 560px — which it is on the Overview, where
                    the rail is 421px on a 1440px screen. The cell keeps its
                    column name instead of losing it with the hidden `thead`.
                  */}
                  <td data-label="Name">{member.name}</td>
                  <td className="mono" data-label="Email">{member.email}</td>
                  <td data-label="Role">{roleLabel(member.project_role)}</td>
                  <td data-label="Financial access">{member.finance_view ? 'Yes' : 'No'}</td>
                  {(can.edit_member || can.remove_member) && (
                    <td className="row-actions">
                      {can.edit_member && (
                        <button
                          type="button"
                          className="link-button"
                          disabled={busy}
                          onClick={() => {
                            setError(null);
                            setEditing(member.user_id);
                          }}
                        >
                          Change<span className="visually-hidden"> {member.name}</span>
                        </button>
                      )}
                      {can.remove_member && (
                        <button
                          type="button"
                          className="link-button danger"
                          disabled={busy}
                          onClick={() => void run(() => removeProjectMember(projectId, member.user_id))}
                        >
                          Remove<span className="visually-hidden"> {member.name}</span>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

interface AddInput {
  readonly user_id: string;
  readonly project_role: string;
  readonly finance_view: boolean;
}

interface AddFormProps {
  readonly projectId: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: AddInput) => void | Promise<void>;
}

function AddMemberForm({ projectId, busy, onCancel, onSubmit }: AddFormProps): JSX.Element {
  const [candidates, setCandidates] = useState<readonly MemberCandidate[] | null>(null);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<Role>('site_engineer');
  /**
   * `null` means "nobody has touched this", and the role's own default applies
   * — a QS starts with financial access, a site engineer does not (`04`
   * §Financial field gating). Storing a plain boolean instead would send an
   * explicit value every time and quietly override those defaults.
   */
  const [financeView, setFinanceView] = useState<boolean | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await listMemberCandidates(projectId);
        if (cancelled) return;
        setCandidates(list);
        // Preselect the first person AND their company role, because the role
        // someone holds in the company is nearly always the one they hold on
        // the project too.
        const first = list[0];
        if (first) {
          setUserId(first.user_id);
          if ((ROLES as readonly string[]).includes(first.company_role)) {
            setRole(first.company_role as Role);
          }
        }
      } catch {
        if (!cancelled) setLoadError('Could not load the list of colleagues.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // The rule, visible in the form rather than only in the error it would
  // return: a client or subcontractor can never be given financial access.
  const mayHoldFinance = canHoldFinanceView(role);
  const effectiveFinance = mayHoldFinance && (financeView ?? defaultFinanceView(role));

  function pickPerson(id: string): void {
    setUserId(id);
    const person = candidates?.find((c) => c.user_id === id);
    if (person && (ROLES as readonly string[]).includes(person.company_role)) {
      setRole(person.company_role as Role);
    }
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!userId) return;
    void onSubmit({ user_id: userId, project_role: role, finance_view: effectiveFinance });
  }

  if (loadError) {
    return (
      <div className="alert" role="alert">
        <span>{loadError}</span>
      </div>
    );
  }

  if (candidates === null) return <p aria-live="polite">Loading colleagues…</p>;

  if (candidates.length === 0) {
    return (
      <div className="panel">
        <p>Everyone in your company is already on this project.</p>
        <div className="button-row">
          <button type="button" className="secondary inline" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field-row">
        <div className="field">
          <label htmlFor="member-person">Person</label>
          <select
            id="member-person"
            value={userId}
            onChange={(e) => pickPerson(e.target.value)}
            disabled={busy}
          >
            {candidates.map((c) => (
              <option key={c.user_id} value={c.user_id}>
                {c.name} — {c.email}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="member-role">Role on this project</label>
          <select
            id="member-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            disabled={busy}
          >
            {ROLES.map((key) => (
              <option key={key} value={key}>
                {roleLabel(key)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <FinanceCheckbox
        id="member-finance"
        checked={effectiveFinance}
        onChange={setFinanceView}
        role={role}
        disabled={busy || !mayHoldFinance}
      />

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !userId}>
          {busy ? 'Adding…' : 'Add to project'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

interface EditRowProps {
  readonly member: ProjectMember;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: { project_role: string; finance_view: boolean }) => void | Promise<void>;
}

function EditMemberRow({ member, busy, onCancel, onSubmit }: EditRowProps): JSX.Element {
  const [role, setRole] = useState<Role>(member.project_role as Role);
  const [financeView, setFinanceView] = useState(member.finance_view);

  const mayHoldFinance = canHoldFinanceView(role);
  const effectiveFinance = mayHoldFinance && financeView;

  return (
    <tr className="editing">
      <td data-label="Name">{member.name}</td>
      <td className="mono" data-label="Email">{member.email}</td>
      <td data-label="Role">
        <label className="visually-hidden" htmlFor={`role-${member.user_id}`}>
          Role for {member.name}
        </label>
        <select
          id={`role-${member.user_id}`}
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          disabled={busy}
        >
          {ROLES.map((key) => (
            <option key={key} value={key}>
              {roleLabel(key)}
            </option>
          ))}
        </select>
      </td>
      <td data-label="Financial access">
        <FinanceCheckbox
          id={`finance-${member.user_id}`}
          checked={effectiveFinance}
          onChange={setFinanceView}
          role={role}
          disabled={busy || !mayHoldFinance}
          compact
        />
      </td>
      <td className="row-actions">
        <button
          type="button"
          className="link-button"
          disabled={busy}
          onClick={() => void onSubmit({ project_role: role, finance_view: effectiveFinance })}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="link-button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------

interface FinanceProps {
  readonly id: string;
  readonly checked: boolean;
  readonly onChange: (value: boolean) => void;
  readonly role: Role;
  readonly disabled: boolean;
  readonly compact?: boolean;
}

function FinanceCheckbox({
  id,
  checked,
  onChange,
  role,
  disabled,
  compact,
}: FinanceProps): JSX.Element {
  const forbidden = !canHoldFinanceView(role);
  return (
    <div className={compact ? 'check compact' : 'check'}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label htmlFor={id}>
        Can see financial figures
        {forbidden && (
          <span className="hint">
            {roleLabel(role)} is outside your company and can never be given financial access.
          </span>
        )}
      </label>
    </div>
  );
}
